import { useFormikContext } from 'formik';
import { t as translate } from 'i18next';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TextInput } from 'shared-ui/lib/components/customs/form/TextInput';
import { setupQueryConfig } from 'shared-ui/lib/config/query.config';
import { FormInput } from 'shared-ui/lib/customs/form/FormInput';
import * as Yup from 'yup';
import { useGroupMemberOptionsQuery } from '../generated/graphql';
import { UserLevel } from '../hooks/use-can';

type GroupFormValues = {
  name?: string;
  isDefault?: boolean;
  vehicleIds?: string[];
  userIds?: string[];
};

const isMasterUser = (user: { level?: string | null; isBusinessAccountOwner: boolean }): boolean =>
  user.isBusinessAccountOwner || user.level === UserLevel.MASTER;

const lockedNameClassName =
  'cursor-not-allowed [&_*]:cursor-not-allowed [&_label]:text-gray-600 [&_input]:pointer-events-none [&_input]:bg-gray-200 [&_input]:text-gray-600 [&_input]:border-gray-400 [&_input]:shadow-none';

const lockedListClassName =
  'mt-2 max-h-64 space-y-2 overflow-y-auto rounded-md border border-gray-300 bg-gray-100 p-3';
const listClassName =
  'mt-2 max-h-64 space-y-2 overflow-y-auto rounded-md border border-gray-200 p-3';
const lockedRowClassName =
  'relative flex cursor-not-allowed select-none items-start rounded-md bg-gray-300 px-2 py-1.5';
const rowClassName =
  'relative flex cursor-pointer items-start rounded-md px-2 py-1.5 hover:bg-gray-50';
const lockedCheckboxClassName =
  'h-4 w-4 cursor-not-allowed rounded border-gray-400 bg-gray-300 text-gray-400';
const checkboxClassName =
  'h-4 w-4 cursor-pointer rounded border-gray-300 text-indigo-600 focus:ring-indigo-500';

const GroupNameInput = () => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<GroupFormValues>();
  const locked = !!values.isDefault;
  const defaultName = t('Group:defaultName');

  useEffect(() => {
    if (!locked || values.name === defaultName) return;
    setFieldValue('name', defaultName);
  }, [locked, defaultName, values.name, setFieldValue]);

  return (
    <div
      className={locked ? lockedNameClassName : undefined}
      title={locked ? t('Group:lockedName') : undefined}
    >
      <fieldset disabled={locked} className="m-0 min-w-0 border-0 p-0">
        <TextInput name="name" label="Group:name" type="text" />
      </fieldset>
      {locked ? <p className="mt-1 text-xs text-gray-600">{t('Group:lockedName')}</p> : undefined}
    </div>
  );
};

const GroupMembersInput = () => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<GroupFormValues>();
  const { data, loading } = useGroupMemberOptionsQuery(setupQueryConfig());
  const vehicles = useMemo(
    () => data?.groupAssignableVehicles.list ?? [],
    [data?.groupAssignableVehicles.list]
  );
  const users = useMemo(() => data?.users.list ?? [], [data?.users.list]);
  const selectedVehicleIds = useMemo(() => values.vehicleIds ?? [], [values.vehicleIds]);
  const selectedUserIds = useMemo(() => values.userIds ?? [], [values.userIds]);
  const isDefault = !!values.isDefault;

  useEffect(() => {
    if (!isDefault || vehicles.length === 0) return;
    const allIds = vehicles.map((vehicle) => vehicle.id);
    const missing = allIds.filter((id) => !selectedVehicleIds.includes(id));
    if (missing.length) {
      setFieldValue('vehicleIds', [...selectedVehicleIds, ...missing]);
    }
  }, [isDefault, vehicles, selectedVehicleIds, setFieldValue]);

  useEffect(() => {
    if (!isDefault) return;
    const masterIds = users.filter(isMasterUser).map((user) => user.id);
    const missing = masterIds.filter((id) => !selectedUserIds.includes(id));
    if (missing.length) {
      setFieldValue('userIds', [...selectedUserIds, ...missing]);
    }
  }, [isDefault, users, selectedUserIds, setFieldValue]);

  const toggleVehicle = (id: string, checked: boolean) => {
    if (isDefault) return;
    if (checked) {
      setFieldValue('vehicleIds', [...selectedVehicleIds, id]);
      return;
    }
    setFieldValue(
      'vehicleIds',
      selectedVehicleIds.filter((vehicleId) => vehicleId !== id)
    );
  };

  const toggleUser = (id: string, checked: boolean, locked: boolean) => {
    if (locked) return;
    if (checked) {
      setFieldValue('userIds', [...selectedUserIds, id]);
      return;
    }
    setFieldValue(
      'userIds',
      selectedUserIds.filter((userId) => userId !== id)
    );
  };

  return (
    <>
      <div>
        <p className="block text-sm font-medium text-gray-700">{t('Group:vehicles')}</p>
        {isDefault ? (
          <p className="mt-1 text-xs text-gray-600">{t('Group:lockedVehicle')}</p>
        ) : undefined}
        <div className={isDefault ? lockedListClassName : listClassName}>
          {loading ? <p className="text-sm text-gray-color">{t('Group:loading')}</p> : undefined}
          {!loading && vehicles.length === 0 ? (
            <p className="text-sm text-gray-color">{t('Group:noVehicles')}</p>
          ) : undefined}
          {vehicles.map((vehicle) => {
            const label =
              [vehicle.name, vehicle.registerNumber].filter(Boolean).join(' · ') || vehicle.id;
            const checked = isDefault || selectedVehicleIds.includes(vehicle.id);

            return (
              <label
                key={vehicle.id}
                className={isDefault ? lockedRowClassName : rowClassName}
                title={isDefault ? t('Group:lockedVehicle') : undefined}
              >
                <div className="flex h-5 items-center">
                  <input
                    type="checkbox"
                    className={isDefault ? lockedCheckboxClassName : checkboxClassName}
                    checked={checked}
                    disabled={isDefault}
                    onChange={(event) => toggleVehicle(vehicle.id, event.target.checked)}
                  />
                </div>
                <span className={`ml-3 text-sm ${isDefault ? 'text-gray-600' : 'text-gray-700'}`}>
                  {label}
                </span>
              </label>
            );
          })}
        </div>
      </div>
      <div>
        <p className="block text-sm font-medium text-gray-700">{t('Group:users')}</p>
        {isDefault ? (
          <p className="mt-1 text-xs text-gray-600">{t('Group:lockedMaster')}</p>
        ) : undefined}
        <div className={listClassName}>
          {loading ? <p className="text-sm text-gray-color">{t('Group:loading')}</p> : undefined}
          {!loading && users.length === 0 ? (
            <p className="text-sm text-gray-color">{t('Group:noUsers')}</p>
          ) : undefined}
          {users.map((user) => {
            const locked = isDefault && isMasterUser(user);
            const checked = locked || selectedUserIds.includes(user.id);
            const label = user.email ? `${user.name} (${user.email})` : user.name;

            return (
              <label
                key={user.id}
                className={locked ? lockedRowClassName : rowClassName}
                title={locked ? t('Group:lockedMaster') : undefined}
              >
                <div className="flex h-5 items-center">
                  <input
                    type="checkbox"
                    className={locked ? lockedCheckboxClassName : checkboxClassName}
                    checked={checked}
                    disabled={locked}
                    onChange={(event) => toggleUser(user.id, event.target.checked, locked)}
                  />
                </div>
                <span className={`ml-3 text-sm ${locked ? 'text-gray-600' : 'text-gray-700'}`}>
                  {label}
                </span>
              </label>
            );
          })}
        </div>
      </div>
    </>
  );
};

const name: FormInput = {
  type: 'element',
  props: {
    type: 'element',
    name: 'name',
    Element: GroupNameInput,
  },
};

const notificationEmail: FormInput = {
  type: 'text',
  initValue: '',
  validation: Yup.string()
    .transform((value) => (value === '' ? null : value))
    .nullable()
    .email(() => translate('Group:invalidEmail')),
  props: {
    name: 'notificationEmail',
    label: 'Group:notificationEmail',
    type: 'email',
  },
};

const members: FormInput = {
  type: 'element',
  props: {
    type: 'element',
    name: 'members',
    Element: GroupMembersInput,
  },
};

export const groupInputs = {
  name,
  notificationEmail,
  members,
};
